import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { api, getErrMsg } from "../lib/api";
import { logError } from "../lib/logger";
import { formatTL, formatDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { PersonelKartDialog } from "../components/PersonelKartDialog";
import {
  Users,
  Wallet,
  PlusCircle,
  Trash2,
  Loader2,
  ShieldCheck,
  Coins,
  CalendarDays,
  Save,
  Sigma,
  IdCard,
  Link2,
  FileDown,
} from "lucide-react";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const AYLAR_KISA = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const YILLAR = [2024, 2025, 2026, 2027];

/* Hesap kuralları (Excel — İşçilik sayfası):
   Nakit Tamamlama = Anlaşılan Net − Banka Ödemesi  (Anlaşılan boşsa 0)
   Toplam Maliyet  = Bordro Brüt + Nakit Tamamlama + Ek Fayda/Prim
   Net Ödenecek    = Anlaşılan Net + Ek Fayda/Prim − Ay İçi Avans */
const hesapla = (anlasilan, brut, banka, ek, avans) => {
  const an = anlasilan === "" || anlasilan == null ? 0 : parseFloat(anlasilan) || 0;
  const br = parseFloat(brut) || 0;
  const bk = parseFloat(banka) || 0;
  const ef = parseFloat(ek) || 0;
  const av = parseFloat(avans) || 0;
  const nk = anlasilan === "" || anlasilan == null ? 0 : an - bk;
  return {
    nakit: nk,
    toplam: br + ef + nk,
    netOdenecek: an + ef - av,
  };
};

export default function PersonelPage() {
  const [tab, setTab] = useState("ucret");
  const [personeller, setPersoneller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yil, setYil] = useState(String(new Date().getFullYear()));
  const [ucretler, setUcretler] = useState(null);
  const [ucretLoading, setUcretLoading] = useState(true);
  const [avanslar, setAvanslar] = useState([]);

  const fetchPersoneller = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/personeller");
      setPersoneller(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Personel listesi yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUcretler = useCallback(async () => {
    setUcretLoading(true);
    try {
      const [u, a] = await Promise.all([
        api.get("/ucret-kayitlari", { params: { yil: parseInt(yil) } }),
        api.get("/avanslar", { params: { yil: parseInt(yil) } }),
      ]);
      setUcretler(u.data);
      setAvanslar(a.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Ücret kayıtları yüklenemedi."));
    } finally {
      setUcretLoading(false);
    }
  }, [yil]);

  useEffect(() => {
    fetchPersoneller();
  }, [fetchPersoneller]);

  useEffect(() => {
    fetchUcretler();
  }, [fetchUcretler]);

  const aktifPersoneller = useMemo(() => (personeller || []).filter((p) => p.aktif), [personeller]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge className="bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/15 hover:bg-[hsl(var(--primary))]/5 gap-1.5" data-testid="personel-kvkk-badge">
          <ShieldCheck className="h-3.5 w-3.5" />
          KVKK — Bu modüle yalnızca Yönetici rolü erişebilir
        </Badge>
        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 gap-1" data-testid="personel-butce-baglanti-badge">
          <Link2 className="h-3 w-3" />
          Bütçe "Brüt Ücret ve Maaş Ödemeleri" kalemi bu modülden otomatik beslenir
        </Badge>
        <div className="ml-auto flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Yıl</Label>
          <Select value={yil} onValueChange={setYil}>
            <SelectTrigger className="h-10 w-28" data-testid="personel-yil-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {YILLAR.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-12 bg-secondary">
          <TabsTrigger value="ucret" className="h-10 gap-1.5 px-3 sm:px-4" data-testid="personel-tab-ucret">
            <Wallet className="h-4 w-4" />
            Aylık Bordro Girişi
          </TabsTrigger>
          <TabsTrigger value="yillik" className="h-10 gap-1.5 px-3 sm:px-4" data-testid="personel-tab-yillik">
            <CalendarDays className="h-4 w-4" />
            Yıllık Görünüm
          </TabsTrigger>
          <TabsTrigger value="liste" className="h-10 gap-1.5 px-3 sm:px-4" data-testid="personel-tab-liste">
            <Users className="h-4 w-4" />
            Personel Listesi {personeller ? `(${personeller.length})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ucret" className="mt-4">
          <AylikUcretGirisi
            aktifPersoneller={aktifPersoneller}
            ucretler={ucretler || []}
            avanslar={avanslar}
            yil={parseInt(yil)}
            loading={loading || ucretLoading}
            onSaved={fetchUcretler}
          />
        </TabsContent>

        <TabsContent value="yillik" className="mt-4">
          <YillikGorunum
            personeller={personeller || []}
            ucretler={ucretler || []}
            yil={parseInt(yil)}
            loading={loading || ucretLoading}
          />
        </TabsContent>

        <TabsContent value="liste" className="mt-4">
          <PersonelListe
            personeller={personeller}
            loading={loading}
            onChanged={() => {
              fetchPersoneller();
              fetchUcretler();
            }}
          />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}

/* ================= AYLIK BORDRO GİRİŞİ (satır içi tablo) ================= */

function AylikUcretGirisi({ aktifPersoneller, ucretler, avanslar, yil, loading, onSaved }) {
  const simdi = new Date();
  const [ay, setAy] = useState(String(simdi.getMonth() + 1));
  const [form, setForm] = useState({}); // pid -> {anlasilan, brut, banka, ek}
  const [dirty, setDirty] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const tabloRef = useRef(null);

  const ayInt = parseInt(ay);

  // OTOMATİK BORDRO DEVRALMA: seçilen ayda kaydı olmayan personellere önceki ayın
  // değerleri otomatik gelir ("değiştirene kadar otomatik" kuralı).
  const devralindiRef = useRef(new Set());
  useEffect(() => {
    const anahtar = `${yil}-${ayInt}`;
    if (devralindiRef.current.has(anahtar)) return;
    devralindiRef.current.add(anahtar);
    api
      .post("/ucret-kayitlari/devral", null, { params: { yil, ay: ayInt } })
      .then((res) => {
        if (res.data?.kopyalanan > 0) {
          toast.info(`${res.data.kopyalanan} personelin bordrosu önceki aydan otomatik devralındı. Zam/değişiklik yaparsanız sonraki aylar yeni değeri alır.`);
          onSaved();
        }
      })
      .catch((err) => logError("Bordro devralma başarısız:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yil, ayInt]);

  const kayitMap = useMemo(() => {
    const m = {};
    ucretler.filter((u) => u.ay === ayInt).forEach((u) => (m[u.personel_id] = u));
    return m;
  }, [ucretler, ayInt]);

  // personel_id -> seçili ayın avans toplamı
  const avansMap = useMemo(() => {
    const m = {};
    avanslar.forEach((a) => {
      const aAy = parseInt((a.tarih || "").slice(5, 7));
      if (aAy === ayInt) m[a.personel_id] = (m[a.personel_id] || 0) + a.tutar;
    });
    return m;
  }, [avanslar, ayInt]);

  useEffect(() => {
    const f = {};
    aktifPersoneller.forEach((p) => {
      const k = kayitMap[p.id];
      f[p.id] = {
        anlasilan: k?.anlasilan_net != null ? String(k.anlasilan_net) : "",
        brut: k?.bordro_brut != null ? String(k.bordro_brut) : "",
        banka: k?.banka_net != null ? String(k.banka_net) : "",
        ek: k?.ek_fayda != null && k.ek_fayda !== 0 ? String(k.ek_fayda) : "",
        avans: k?.avans != null && k.avans !== 0 ? String(k.avans) : (avansMap[p.id] ? String(avansMap[p.id]) : ""),
      };
    });
    setForm(f);
    setDirty(new Set());
  }, [aktifPersoneller, kayitMap, avansMap]);

  const setAlan = (pid, alan, deger) => {
    setForm((f) => ({ ...f, [pid]: { ...f[pid], [alan]: deger } }));
    setDirty((d) => new Set(d).add(pid));
  };

  const ayToplamMaliyet = useMemo(
    () =>
      aktifPersoneller.reduce((acc, p) => {
        const v = form[p.id];
        if (!v) return acc;
        return acc + hesapla(v.anlasilan, v.brut, v.banka, v.ek, 0).toplam;
      }, 0),
    [aktifPersoneller, form]
  );

  const yillikKumulatif = useMemo(
    () => ucretler.reduce((acc, u) => acc + (u.toplam_maliyet || 0), 0),
    [ucretler]
  );

  const girilenAdet = aktifPersoneller.filter((p) => kayitMap[p.id]).length;

  const handleKaydet = async () => {
    const kaydedilecek = Array.from(dirty).filter((pid) => {
      const v = form[pid];
      return v && (v.anlasilan !== "" || v.brut !== "" || v.banka !== "" || v.ek !== "" || v.avans !== "");
    });
    if (kaydedilecek.length === 0) {
      toast.info("Kaydedilecek değişiklik yok.");
      return;
    }
    setSaving(true);
    let hata = 0;
    for (const pid of kaydedilecek) {
      const v = form[pid];
      try {
        await api.post("/ucret-kayitlari", {
          personel_id: pid,
          yil,
          ay: ayInt,
          anlasilan_net: v.anlasilan === "" ? null : parseFloat(v.anlasilan),
          bordro_brut: v.brut === "" ? null : parseFloat(v.brut),
          banka_net: v.banka === "" ? null : parseFloat(v.banka),
          ek_fayda: v.ek === "" ? null : parseFloat(v.ek),
          avans: v.avans === "" ? null : parseFloat(v.avans),
        });
      } catch {
        hata += 1;
      }
    }
    setSaving(false);
    if (hata === 0) toast.success(`${kaydedilecek.length} personelin bordro kaydı kaydedildi. Bütçedeki personel kalemi güncellendi.`);
    else toast.error(`${hata} kayıt kaydedilemedi.`);
    onSaved();
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/ucret-kayitlari/${deleteTarget.id}`);
      toast.success("Bordro kaydı silindi. Bütçe güncellendi.");
      setDeleteTarget(null);
      onSaved();
    } catch (err) {
      toast.error(getErrMsg(err, "Kayıt silinemedi."));
    }
  };

  // Bordro tablosunu A4 yatay PDF olarak dışa aktarır
  const handleBordroPdf = async () => {
    if (!tabloRef.current) return;
    setPdfLoading(true);
    try {
      const canvas = await html2canvas(tabloRef.current, {
        scale: 2,
        backgroundColor: "#FFFFFF",
        useCORS: true,
        logging: false,
      });
      const img = canvas.toDataURL("image/jpeg", 0.9);
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const w = 287; // 5mm kenar boşluğu
      const h = 200;
      const imgH = (canvas.height * w) / canvas.width;
      let pos = 0;
      while (pos < imgH) {
        if (pos > 0) pdf.addPage();
        pdf.addImage(img, "JPEG", 5, 5 - pos, w, imgH);
        pos += h;
      }
      pdf.save(`3A-Bordro-${AYLAR[ayInt - 1]}-${yil}.pdf`);
      toast.success("Bordro PDF indirildi.");
    } catch (err) {
      logError(err);
      toast.error("PDF oluşturulamadı.");
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ucret-ozet-kartlar">
        <OzetKart
          icon={Coins}
          renk="#7C3AED"
          baslik={`${AYLAR[ayInt - 1]} Toplam Maliyet`}
          deger={formatTL(ayToplamMaliyet)}
          alt="Bordro brüt + nakit tamamlama + ek fayda"
          testid="ucret-ozet-ay-maliyet"
        />
        <OzetKart
          icon={Sigma}
          renk="#E2B23C"
          baslik={`${yil} Yıllık Kümülatif`}
          deger={formatTL(yillikKumulatif)}
          alt="Yıl boyu kaydedilen toplam maliyet"
          testid="ucret-ozet-yillik-kumulatif"
        />
        <OzetKart
          icon={Users}
          renk="#2563EB"
          baslik="Kişi Sayısı"
          deger={`${aktifPersoneller.length}`}
          alt={`${girilenAdet} personel için bu ay kayıt girildi`}
          testid="ucret-ozet-kisi"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Ay</Label>
          <Select value={ay} onValueChange={setAy}>
            <SelectTrigger className="h-10 w-36" data-testid="ucret-ay-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              {AYLAR.map((a, i) => (
                <SelectItem key={a} value={String(i + 1)}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Badge className="bg-muted text-muted-foreground border border-border hover:bg-muted" data-testid="ucret-oran-badge">
          Excel kuralı: Nakit Tamamlama = Anlaşılan Net − Banka • Toplam = Brüt + Nakit + Ek Fayda
        </Badge>
        <Button
          onClick={handleBordroPdf}
          disabled={pdfLoading || aktifPersoneller.length === 0}
          variant="outline"
          className="ml-auto min-h-[44px] gap-2"
          data-testid="bordro-pdf-button"
        >
          {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
          Bordro PDF
        </Button>
        <Button
          onClick={handleKaydet}
          disabled={saving || dirty.size === 0}
          className="min-h-[44px] gap-2"
          data-testid="ucret-kaydet-button"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Kaydediliyor…" : `Değişiklikleri Kaydet${dirty.size > 0 ? ` (${dirty.size})` : ""}`}
        </Button>
      </div>

      <div ref={tabloRef} className="rounded-xl border bg-card overflow-hidden" data-testid="ucret-tablo">
        <div className="px-4 pt-3 pb-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--primary))]">3A Otomotiv — Personel Bordrosu</p>
          <h3 className="font-display text-sm font-semibold">Bordro Girişi — {AYLAR[ayInt - 1]} {yil}</h3>
          <p className="text-[11px] text-muted-foreground">
            Nakit Tamamlama = Anlaşılan Net − Banka Ödemesi (otomatik) • Toplam Maliyet = Bordro Brüt + Nakit Tamamlama + Ek Fayda/Prim • Net Ödenecek = Anlaşılan Net + Ek Fayda/Prim − Avans
          </p>
        </div>
        {aktifPersoneller.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="ucret-bos-durum">
            Aktif personel bulunmuyor. Önce "Personel Listesi" sekmesinden personel ekleyin.
          </div>
        ) : (
          <div className="w-full overflow-x-auto mt-1">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 min-w-[150px]">Personel</th>
                  <th className="px-2 py-2.5 text-right min-w-[120px]">Anlaşılan Net ₺</th>
                  <th className="px-2 py-2.5 text-right min-w-[120px]">Bordro Brüt ₺</th>
                  <th className="px-2 py-2.5 text-right min-w-[120px]">Banka Net ₺</th>
                  <th className="px-3 py-2.5 text-right min-w-[125px] bg-teal-50/70 text-teal-800">Nakit Tamamlama</th>
                  <th className="px-2 py-2.5 text-right min-w-[115px]">Ek Fayda / Prim ₺</th>
                  <th className="px-3 py-2.5 text-right min-w-[100px] bg-red-50/60 text-red-700">Avans (Ay)</th>
                  <th className="px-3 py-2.5 text-right min-w-[115px] bg-blue-50/60 text-blue-800">Net Ödenecek</th>
                  <th className="px-3 py-2.5 text-right min-w-[125px] bg-[hsl(var(--accent))]/10 text-[hsl(var(--primary))]">Toplam Maliyet</th>
                  <th className="px-2 py-2.5 w-10" />
                </tr>
              </thead>
              <tbody>
                {aktifPersoneller.map((p) => {
                  const v = form[p.id] || { anlasilan: "", brut: "", banka: "", ek: "", avans: "" };
                  const h = hesapla(v.anlasilan, v.brut, v.banka, v.ek, v.avans);
                  const kayit = kayitMap[p.id];
                  const degisti = dirty.has(p.id);
                  return (
                    <tr key={p.id} className={`border-t border-border/50 ${degisti ? "bg-amber-50/40" : ""}`} data-testid={`ucret-satir-${p.id}`}>
                      <td className="px-4 py-1.5 font-medium whitespace-nowrap">
                        {p.ad_soyad}
                        {degisti && <span className="ml-1.5 text-[10px] font-semibold text-amber-600">●</span>}
                        {kayit?.otomatik_devir && !degisti && (
                          <span
                            className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-sky-50 text-sky-700 border border-sky-200"
                            title={`Önceki aydan otomatik devralındı (${kayit.devir_kaynak}). Değer girerseniz kalıcılaşır ve sonraki aylar yeni değeri alır.`}
                            data-testid={`ucret-otomatik-badge-${p.id}`}
                          >
                            Oto
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number" inputMode="decimal" step="0.01" min="0" placeholder="—"
                          value={v.anlasilan}
                          onChange={(e) => setAlan(p.id, "anlasilan", e.target.value)}
                          className="h-9 w-full min-w-[105px] text-right tabular-nums text-sm"
                          data-testid={`ucret-input-anlasilan-${p.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number" inputMode="decimal" step="0.01" min="0" placeholder="—"
                          value={v.brut}
                          onChange={(e) => setAlan(p.id, "brut", e.target.value)}
                          className="h-9 w-full min-w-[105px] text-right tabular-nums text-sm"
                          data-testid={`ucret-input-brut-${p.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number" inputMode="decimal" step="0.01" min="0" placeholder="—"
                          value={v.banka}
                          onChange={(e) => setAlan(p.id, "banka", e.target.value)}
                          className="h-9 w-full min-w-[105px] text-right tabular-nums text-sm"
                          data-testid={`ucret-input-banka-${p.id}`}
                        />
                      </td>
                      <td className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap font-semibold bg-teal-50/40 ${h.nakit < 0 ? "text-red-600" : "text-teal-800"}`} data-testid={`ucret-nakit-${p.id}`}>
                        {v.anlasilan === "" ? "—" : formatTL(h.nakit)}
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="number" inputMode="decimal" step="0.01" min="0" placeholder="—"
                          value={v.ek}
                          onChange={(e) => setAlan(p.id, "ek", e.target.value)}
                          className="h-9 w-full min-w-[100px] text-right tabular-nums text-sm"
                          data-testid={`ucret-input-ek-${p.id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 bg-red-50/40">
                        <Input
                          type="number" inputMode="decimal" step="0.01" min="0" placeholder="—"
                          value={v.avans}
                          onChange={(e) => setAlan(p.id, "avans", e.target.value)}
                          className="h-9 w-full min-w-[95px] text-right tabular-nums text-sm text-red-700"
                          data-testid={`ucret-input-avans-${p.id}`}
                        />
                      </td>
                      <td className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap bg-blue-50/40 ${h.netOdenecek < 0 ? "text-red-600" : "text-blue-800"}`} data-testid={`ucret-net-odenecek-${p.id}`}>
                        <span className="font-semibold">{formatTL(h.netOdenecek)}</span>
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap font-bold bg-[hsl(var(--accent))]/5" data-testid={`ucret-toplam-${p.id}`}>
                        {formatTL(h.toplam)}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {kayit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600"
                            onClick={() => setDeleteTarget(kayit)}
                            data-testid={`ucret-sil-${p.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {/* TOPLAM SATIRI */}
                <tr className="border-t-2 border-[hsl(var(--primary))]/20 bg-[hsl(var(--primary))] text-white font-semibold" data-testid="ucret-toplam-satiri">
                  <td className="px-4 py-2.5">TOPLAM ({aktifPersoneller.length} kişi)</td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {formatTL(aktifPersoneller.reduce((a, p) => a + (parseFloat(form[p.id]?.anlasilan) || 0), 0))}
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {formatTL(aktifPersoneller.reduce((a, p) => a + (parseFloat(form[p.id]?.brut) || 0), 0))}
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {formatTL(aktifPersoneller.reduce((a, p) => a + (parseFloat(form[p.id]?.banka) || 0), 0))}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {formatTL(aktifPersoneller.reduce((a, p) => {
                      const v = form[p.id];
                      return a + (v ? hesapla(v.anlasilan, v.brut, v.banka, v.ek, 0).nakit : 0);
                    }, 0))}
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {formatTL(aktifPersoneller.reduce((a, p) => a + (parseFloat(form[p.id]?.ek) || 0), 0))}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {formatTL(aktifPersoneller.reduce((a, p) => a + (parseFloat(form[p.id]?.avans) || 0), 0))}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                    {formatTL(aktifPersoneller.reduce((a, p) => {
                      const v = form[p.id];
                      return a + (v ? hesapla(v.anlasilan, v.brut, v.banka, v.ek, v.avans).netOdenecek : 0);
                    }, 0))}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-[hsl(var(--accent))]">
                    {formatTL(ayToplamMaliyet)}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="ucret-sil-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bordro kaydı silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Seçili ayın bordro kaydı kalıcı olarak silinecek ve bütçedeki personel kalemi güncellenecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ucret-sil-vazgec">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="ucret-sil-onayla">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ================= YILLIK GÖRÜNÜM (personel × 12 ay matris) ================= */

const BLOKLAR = [
  { key: "anlasilan_net", etiket: "Anlaşılan Net" },
  { key: "bordro_brut", etiket: "Bordro Brüt" },
  { key: "banka_net", etiket: "Banka Net" },
  { key: "nakit_tamamlama", etiket: "Nakit Tamamlama" },
  { key: "ek_fayda", etiket: "Ek Fayda / Prim" },
  { key: "toplam_maliyet", etiket: "Toplam Maliyet" },
];

function YillikGorunum({ personeller, ucretler, yil, loading }) {
  const [blok, setBlok] = useState("toplam_maliyet");

  const matris = useMemo(() => {
    const m = {};
    ucretler.forEach((u) => {
      if (!m[u.personel_id]) m[u.personel_id] = {};
      m[u.personel_id][u.ay] = u;
    });
    return m;
  }, [ucretler]);

  const satirPersoneller = useMemo(
    () => personeller.filter((p) => p.aktif || matris[p.id]),
    [personeller, matris]
  );

  const deger = (pid, ayNo) => {
    const k = matris[pid]?.[ayNo];
    if (!k) return null;
    const v = k[blok];
    return v == null ? null : v;
  };

  const ayToplamlari = useMemo(() => {
    const t = Array(12).fill(0);
    satirPersoneller.forEach((p) => {
      for (let m = 1; m <= 12; m++) {
        t[m - 1] += deger(p.id, m) || 0;
      }
    });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [satirPersoneller, matris, blok]);

  const genelToplam = ayToplamlari.reduce((a, b) => a + b, 0);

  if (loading) {
    return <Skeleton className="h-80 w-full" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm font-medium">Görünüm:</p>
        <div className="flex items-center gap-1 overflow-x-auto rounded-lg border bg-card p-1" data-testid="yillik-blok-secici">
          {BLOKLAR.map((b) => (
            <button
              key={b.key}
              onClick={() => setBlok(b.key)}
              data-testid={`yillik-blok-${b.key}`}
              className={`shrink-0 rounded-md px-3 py-2 text-xs font-medium transition-colors min-h-[36px] ${
                blok === b.key
                  ? "bg-[hsl(var(--accent))] text-[hsl(var(--primary))] font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {b.etiket}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border bg-card overflow-hidden" data-testid="yillik-matris">
        <div className="border-b bg-[hsl(var(--primary))] px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--accent))]">
            {BLOKLAR.find((b) => b.key === blok)?.etiket} — {yil} • Personel × Ay Matrisi
          </span>
        </div>
        {satirPersoneller.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="yillik-bos-durum">
            Görüntülenecek personel/ücret kaydı bulunmuyor.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="sticky left-0 z-10 bg-muted/60 backdrop-blur px-3 py-2.5 min-w-[150px] border-r">Personel</th>
                  {AYLAR_KISA.map((a) => (
                    <th key={a} className="px-2 py-2.5 text-right min-w-[80px]">{a}</th>
                  ))}
                  <th className="px-3 py-2.5 text-right min-w-[100px] border-l-2 border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 text-[hsl(var(--primary))] font-bold">
                    Yıllık
                  </th>
                </tr>
              </thead>
              <tbody>
                {satirPersoneller.map((p) => {
                  const yillikToplam = Array.from({ length: 12 }, (_, i) => deger(p.id, i + 1) || 0).reduce((a, b) => a + b, 0);
                  return (
                    <tr key={p.id} className="border-t border-border/50 hover:bg-muted/30" data-testid="yillik-satir">
                      <td className="sticky left-0 z-10 bg-card px-3 py-2 font-medium whitespace-nowrap border-r">
                        {p.ad_soyad}
                        {!p.aktif && <Badge className="ml-1.5 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100 text-[9px] px-1">Pasif</Badge>}
                      </td>
                      {Array.from({ length: 12 }, (_, i) => {
                        const v = deger(p.id, i + 1);
                        return (
                          <td key={i} className={`px-2 py-2 text-right tabular-nums whitespace-nowrap ${v == null ? "text-muted-foreground/40" : blok === "nakit_tamamlama" && v < 0 ? "text-red-600" : ""}`}>
                            {v == null ? "—" : formatTL(v)}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap font-bold border-l-2 border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5">
                        {formatTL(yillikToplam)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-[hsl(var(--accent))] bg-[hsl(var(--primary))] text-white font-semibold" data-testid="yillik-toplam-satiri">
                  <td className="sticky left-0 z-10 bg-[hsl(var(--primary))] px-3 py-2.5 border-r border-white/10 uppercase tracking-wide">Aylık Toplam</td>
                  {ayToplamlari.map((t, i) => (
                    <td key={i} className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap">
                      {t === 0 ? "—" : formatTL(t)}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-[hsl(var(--accent))] font-bold border-l-2 border-[hsl(var(--accent))]/40">
                    {formatTL(genelToplam)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= ÖZET KART ================= */

const OzetKart = ({ icon: Icon, renk, baslik, deger, alt, testid }) => (
  <div className="flex items-center gap-3 rounded-xl border bg-card p-4 shadow-[0_1px_0_rgba(14,27,46,0.06),0_8px_24px_rgba(14,27,46,0.06)]" data-testid={testid}>
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${renk}1A` }}>
      <Icon className="h-5 w-5" style={{ color: renk }} />
    </span>
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate">{baslik}</p>
      <p className="font-display text-lg font-bold tabular-nums whitespace-nowrap">{deger}</p>
      <p className="text-[11px] text-muted-foreground truncate">{alt}</p>
    </div>
  </div>
);

/* ================= PERSONEL LİSTESİ ================= */

function PersonelListe({ personeller, loading, onChanged }) {
  const [dialogTarget, setDialogTarget] = useState(null); // null | "yeni" | personel
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async () => {
    try {
      await api.delete(`/personeller/${deleteTarget.id}`);
      toast.success("Personel silindi.");
      setDeleteTarget(null);
      onChanged();
    } catch (err) {
      toast.error(getErrMsg(err, "Personel silinemedi."));
    }
  };

  const aktifSayi = (personeller || []).filter((p) => p.aktif).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-muted-foreground" data-testid="personel-aktif-bilgi">
          <strong className="text-foreground">{aktifSayi}</strong> aktif / {(personeller || []).length} toplam personel
        </span>
        <Button onClick={() => setDialogTarget("yeni")} className="ml-auto h-10 gap-1.5" data-testid="personel-yeni-button">
          <PlusCircle className="h-4 w-4" />
          Yeni Personel
        </Button>
      </div>

      <div className="rounded-xl border bg-card overflow-hidden" data-testid="personel-tablo">
        {loading ? (
          <div className="space-y-2 p-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (personeller || []).length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="personel-bos-durum">
            Henüz personel kaydı yok. "Yeni Personel" butonuyla ekleyin.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5">Ad Soyad</th>
                  <th className="px-3 py-2.5">Pozisyon</th>
                  <th className="px-3 py-2.5">İşe Giriş</th>
                  <th className="px-3 py-2.5">Telefon</th>
                  <th className="px-3 py-2.5">Durum</th>
                  <th className="px-3 py-2.5 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {personeller.map((p) => (
                  <tr key={p.id} className="border-t border-border/60" data-testid="personel-satiri">
                    <td className="px-4 py-2.5 font-medium whitespace-nowrap">{p.ad_soyad}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{p.pozisyon || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2.5 tabular-nums whitespace-nowrap">{p.ise_giris_tarihi ? formatDate(p.ise_giris_tarihi) : "—"}</td>
                    <td className="px-3 py-2.5 tabular-nums whitespace-nowrap">{p.telefon || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2.5">
                      {p.aktif ? (
                        <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-50">Aktif</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Pasif</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1 text-xs"
                          onClick={() => setDialogTarget(p)}
                          data-testid={`personel-kart-${p.id}`}
                        >
                          <IdCard className="h-3 w-3" />
                          Personel Kartı
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600"
                          onClick={() => setDeleteTarget(p)}
                          data-testid={`personel-sil-${p.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PersonelKartDialog
        target={dialogTarget}
        onClose={() => setDialogTarget(null)}
        onSaved={() => {
          setDialogTarget(null);
          onChanged();
        }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="personel-sil-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Personel silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.ad_soyad}" ve bu personele ait TÜM bordro, avans, izin ve belge kayıtları kalıcı olarak silinecek. Bu işlem geri alınamaz.
              İşten ayrılan personel için silmek yerine "Pasif" yapmanız önerilir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="personel-sil-vazgec">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="personel-sil-onayla">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
